import { isValid, parse } from 'postcode'

import {
  IData_Booking_Availability_DayOfWeek,
  IData_Booking_Availability_Time,
  IData_Booking_Availability_Time_Slot,
  IData_Booking_Availability_Date_Slot,
  IData_Booking_Availability,
  IData_Booking_Pricing_Product,
  IRecord_Booking_Core_Viewable,
  IRecord_Booking_Core_Editable,
  IData_Booking_Settings_ET,
} from '../types/schema/booking'

import {
  IRecord_CRMContact_Import,
} from '../types/schema/crm'

import {
  INode_Org,
  INode_User,
  ITree_Org,
} from '../types/schema/tree'

import {
  getTitle as getPriceTitle,
} from '../utils/price'

import {
  findParentOrg,
} from '../utils/tree'

import {
  BOOKING_DAYS_OF_WEEK_BY_DATE,
  BOOKING_ET_ROLE_NAME,
} from '../constants/booking'

export const DAY = 1000 * 60 * 60 * 24

export const setTimeOfDate = (date: Date, time: IData_Booking_Availability_Time): Date => {
  const ret = new Date(date.getTime())
  ret.setHours(time.hour)
  ret.setMinutes(time.min)
  ret.setSeconds(0)
  return ret
}

export const getBookingDates = (booking: IRecord_Booking_Core_Editable): {
  start: Date,
  end: Date,
} => {
  const {
    date,
    product,
  } = booking
  const startTime = getTimeFromDateString(date)
  const endTime = addLengthToTime(startTime, product.length)
  return {
    start: setTimeOfDate(dateConstructor(date), startTime),
    end: setTimeOfDate(dateConstructor(date), endTime),
  }
}

export const isOrgNodeRoot = (orgNode: INode_Org): boolean => {
  return orgNode.meta?.appconfig?.booking?.is_root_org ? true : false
}

export const padTimeTitle = (time: number) => {
  const ret = time.toString()
  return ret.length == 1 ? '0' + ret : ret
}

export const padDateTitle = padTimeTitle

export const getTimeLengthTitle = (length: number) => {
  length = isNaN(length) ? 0 : length
  const hours = Math.floor(length / 60)
  const minutes = length % 60
  return [
    hours > 0 ? `${hours}h` : null,
    `${padTimeTitle(minutes)}m`
  ].filter(line => line).join(' ')
}

export const getDateDayOfWeek = (dt: string) => {
  const dow = new Date(dt).getDay()
  return BOOKING_DAYS_OF_WEEK_BY_DATE[dow]
}

export const getDateEpochDays = (dt: Date) => Math.floor(dt.getTime() / DAY)
export const getDaysFromToday = (dt: string) => {
  const todayDays = getDateEpochDays(new Date())
  const dtDays = getDateEpochDays(new Date(dt))
  return dtDays - todayDays
}
export const getDaysFromDate = (dt: string, compare: string) => {
  const compareDays = getDateEpochDays(new Date(compare))
  const dtDays = getDateEpochDays(new Date(dt))
  return dtDays - compareDays
}
export const getDateDaysFromToday = (days: number): Date => {
  return new Date(new Date().getTime() + (days * DAY))
}

export const isDateTodayOrBefore = (dt: string) => {
  return getDaysFromToday(dt) <= 0
}

export const isDateBeforeBuffer = (dt: string, bufferZone: number) => {
  const todayDays = getDateEpochDays(new Date())
  const dtDays = getDateEpochDays(new Date(dt))
  const bufferZoneDays = todayDays + bufferZone
  return dtDays < bufferZoneDays
}

export const getShortDayTitle = (dayTitle: IData_Booking_Availability_DayOfWeek) => dayTitle.substr(0, 3)
export const getTimeTitle = (time: IData_Booking_Availability_Time) => `${padTimeTitle(time.hour)}:${padTimeTitle(time.min)}`
export const getTimeSlotTitle = (timeSlot: IData_Booking_Availability_Time_Slot) => `${getTimeTitle(timeSlot.start)} - ${getTimeTitle(timeSlot.end)}`
export const getDateTitle = (dt: string) => dt ? new Date(dt).toLocaleDateString() : ''
export const getDateString = (dt: Date) => {
  if(!dt) return ''
  const adjustDate = new Date(dt.getTime())
  adjustDate.setHours(12)
  return dt ? [
    adjustDate.getUTCFullYear(),
    padDateTitle(adjustDate.getUTCMonth() + 1),
    padDateTitle(adjustDate.getUTCDate()),
  ].join('-') : ''
}
export const getTimeString = (dt: Date, withSeconds = true) => dt ? [
  padTimeTitle(dt.getHours()),
  padTimeTitle(dt.getMinutes()),
  withSeconds ? padTimeTitle(dt.getSeconds()) : null,
].filter(p => p).join(':') : ''
export const getDateTimeString = (dt: Date, join = ' ') => dt ? [
  getDateString(dt),
  getTimeString(dt),
].join(join) : ''

export const getTimeFromDateString = (dtString: string): IData_Booking_Availability_Time => {
  const dt = new Date(dtString)
  const time: IData_Booking_Availability_Time = {
    hour: dt.getHours(),
    min: dt.getMinutes(),
  }
  return time
}

export const addLengthToTime = (time: IData_Booking_Availability_Time, length: number): IData_Booking_Availability_Time => {
  const dt = new Date()
  dt.setHours(time.hour)
  dt.setMinutes(time.min)
  dt.setSeconds(0)
  dt.setMilliseconds(0)
  dt.setTime(dt.getTime() + length * 60 * 1000)
  return {
    hour: dt.getHours(),
    min: dt.getMinutes(),
  }
}

export const getDateRange = (start: string, days: number) => {
  const dates: string[] = []
  const startDate = dateConstructor(start)
  for(let i=0; i<days; i++) {
    const currentDate = dateConstructor(startDate.getTime() + (i * 24 * 60 * 60 * 1000))
    dates.push(getDateString(currentDate))
  }
  return dates
}

export const getDateRangeStartEnd = (start: string, days: number) => {
  const dateRange = getDateRange(start, days)
  return {
    start: dateRange[0],
    end: dateRange[dateRange.length - 1],
  }
}

export const getWeekdaysTitle = (daysOfWeek: IData_Booking_Availability_DayOfWeek[]): string => daysOfWeek.map(getShortDayTitle).join(', ')
export const getTimeslotTitle = (timeSlots: IData_Booking_Availability_Time_Slot[]): string => timeSlots.map(getTimeSlotTitle).join(', ')

export const getBaselineTitle = (baseline: IData_Booking_Availability): string => `${getWeekdaysTitle(baseline.daysOfWeek)} @ ${getTimeslotTitle(baseline.timeSlots)}`
export const getDateSlotsTitle = (dateSlots: IData_Booking_Availability_Date_Slot[]): string => {
  return dateSlots.map(dateSlot => {
    return [
      getDateTitle(dateSlot.start),
      getDateTitle(dateSlot.end),
    ].filter(t => t).join(' - ')
  }).join(', ')
}

export const getOverrideTitle = (override: IData_Booking_Availability): string => {
  // const dateTitle = getOverrideDateTitle(override)
  // //const timeTitle = override.timeSlots.length > 0 ? getTimeslotTitle(override.timeSlots) : 'All Day'
  // return `${dateTitle}`//` @ ${timeTitle}`
  return ''
}

export const getAvailabilityTitle = (availability: IData_Booking_Availability): string => {  
  const daysOfWeek = getWeekdaysTitle(availability.daysOfWeek)
  const dateSlots = getDateSlotsTitle(availability.dateSlots)
  const timeSlots = getTimeslotTitle(availability.timeSlots)
  return [
    daysOfWeek,
    dateSlots,
    availability.timeSlots.length > 0 ? `@ ${timeSlots}` : '',
  ].join(' ')
}

export const getProductTitle = (product: IData_Booking_Pricing_Product, withName = true): string => {
  const details = `${getTimeLengthTitle(product.length)} @ ${getPriceTitle(product.price * 100)}`
  return withName ? `${product.name} (${details})` : details
}

export const getCrmTitle = (crm: IRecord_CRMContact_Import): string => {
  return `${crm.email} ${crm.postcode}`
}

export const getCalendarExportSummary = (booking: IRecord_Booking_Core_Viewable): string => {
  const {
    data: {
      crm,
      reference,
    }
  } = booking
  return `${reference} ${crm.firstname} ${crm.surname} ${crm.postcode}`
}

export const getCalendarExportDescription = (booking: IRecord_Booking_Core_Viewable, url: string): string => {
  const {
    et_data,
    data: {
      reference,
      product,
      crm,
    }
  } = booking

  return ` * ${ getProductTitle(product) }
 * ${ crm.firstname } ${ crm.surname }
 * ${ crm.address1 } ${ crm.address2 } ${ crm.postcode }
 * ${ crm.email } ${ crm.phone_number }
 * ${ getProductTitle(product) }
 
${ url }
`
}

export const isTimeBeforeOrSame = (a: IData_Booking_Availability_Time, b: IData_Booking_Availability_Time) => {
  if(a.hour < b.hour) {
    return true
  }
  else if(a.hour == b.hour) {
    if(a.min <= b.min) {
      return true
    }
  }
  return false
}

export const isTimeAfterOrSame = (a: IData_Booking_Availability_Time, b: IData_Booking_Availability_Time) => {
  if(a.hour > b.hour) {
    return true
  }
  else if(a.hour == b.hour) {
    if(a.min >= b.min) {
      return true
    }
  }
  return false
}

export const dateConstructor = (dateString: string | number): Date => {
  const dt = new Date(dateString)
  dt.setHours(0)
  dt.setMinutes(0)
  dt.setSeconds(0)
  dt.setMilliseconds(0)
  return dt
}

export const getDateDays = (dateString: string | number) => {
  const dt = dateConstructor(dateString)
  return Math.floor(dt.getTime() / 1000 / 60 / 60 / 24)
}

export const doesDateMatchDateSlots = (dateString: string | number, dateSlots: IData_Booking_Availability_Date_Slot[]) => {
  const dateDays = getDateDays(dateString)
  const matchingDateSlot = dateSlots.find(dateSlot => {
    const startDays = getDateDays(dateSlot.start)
    if(!dateSlot.end) return startDays == dateDays
    const endDays = getDateDays(dateSlot.end)
    return startDays <= dateDays && endDays >= dateDays
  })
  return matchingDateSlot ? true : false
}

export const dateAsEpoch = (dateString: string): number => {
  return dateConstructor(dateString).getTime()
}

export const isDateBeforeOrSame = (a: string, b: string) => {
  return dateAsEpoch(a) <= dateAsEpoch(b)
}

export const isDateAfterOrSame = (a: string, b: string) => {
  return dateAsEpoch(a) >= dateAsEpoch(b)
}

export const checkOverlappingDaysOfWeek = (baseline: IData_Booking_Availability, existing: IData_Booking_Availability[]): string => {
  const existingMap = existing.reduce((all, existingBaseline) => {
    existingBaseline.daysOfWeek.forEach(dayOfWeek => {
      all[dayOfWeek] = true
    })
    return all
  }, {})
  return baseline.daysOfWeek.find(dayOfWeek => existingMap[dayOfWeek] ? true : false) ? 'days of week are overlapping' : ''
}

export const checkOverlappingDateRanges = (dateSlots: IData_Booking_Availability_Date_Slot[]): string => {

  const overlappingDateSlots = dateSlots.filter((dateSlotA, i) => {
    const overlappingWith = dateSlots.filter((dateSlotB, j) => {
      if(i == j) return false

      const startA = dateSlotA.start
      const startB = dateSlotB.start
      const endA = dateSlotA.end || dateSlotA.start
      const endB = dateSlotB.end || dateSlotB.start
      
      return isDateBeforeOrSame(startA, endB) && isDateAfterOrSame(endA, startB)
    })
    return overlappingWith.length > 0
  })

  if(overlappingDateSlots.length > 0) return `date slots are overlapping: ${getDateSlotsTitle(overlappingDateSlots)}`

  return ''
}

export const validateTimeslot = (timeSlot: IData_Booking_Availability_Time_Slot): string => {
  if(timeSlot.end.hour < timeSlot.start.hour) {
    return `end time must be after start time: ${getTimeSlotTitle(timeSlot)}`
  }
  else if(timeSlot.end.hour == timeSlot.start.hour) {
    if(timeSlot.end.min <= timeSlot.start.min) {
      return `end time must be after start time: ${getTimeSlotTitle(timeSlot)}`
    }
  }
  return ''
}

export const validateDateslot = (dateSlot: IData_Booking_Availability_Date_Slot): string => {
  if(!dateSlot.end) return ''
  if(isDateAfterOrSame(dateSlot.start, dateSlot.end)) return `end date must be after start date: ${getDateSlotsTitle([dateSlot])}`
  return ''
}

export const validateTimeslots = (timeSlots: IData_Booking_Availability_Time_Slot[]): string => {
  const individualTimeSlotError = timeSlots
    .map(validateTimeslot)
    .find(error => error != '')
  if(individualTimeSlotError) return individualTimeSlotError

  const overlappingTimeSlots = timeSlots.filter((timeSlotA, i) => {
    const overlappingWith = timeSlots.filter((timeSlotB, j) => {
      if(i == j) return false
      return isTimeBeforeOrSame(timeSlotA.start, timeSlotB.end) && isTimeAfterOrSame(timeSlotA.end, timeSlotB.start)
    })
    return overlappingWith.length > 0
  })

  if(overlappingTimeSlots.length > 0) return `time slots are overlapping: ${overlappingTimeSlots.map(getTimeSlotTitle).join(', ')}`

  return ''
}

export const validateDateslots = (dateSlots: IData_Booking_Availability_Date_Slot[]): string => {
  const individualDateSlotError = dateSlots
    .map(validateDateslot)
    .find(error => error != '')
  if(individualDateSlotError) return individualDateSlotError
  return checkOverlappingDateRanges(dateSlots)
}

export const validateBaselineSetting = (baseline: IData_Booking_Availability): string => {
  if(baseline.daysOfWeek.length <= 0) return 'at least one day of week is required'
  if(baseline.timeSlots.length <= 0) return 'at least one time slot is required'
  return validateTimeslots(baseline.timeSlots)
}

export const validateOverride = (override: IData_Booking_Availability): string => {
  const timeslotError = validateTimeslots(override.timeSlots)
  if(timeslotError) return timeslotError
  const dateSlotError = validateDateslots(override.dateSlots)
  if(dateSlotError) return dateSlotError
  if(override.dateSlots.length <=0 ) return 'at least one date slot is required'
  return ''
}

export const checkOverlappingOverrides = (override: IData_Booking_Availability, existing: IData_Booking_Availability[]): string => {
  const existingDateSlots = existing.reduce((all, existingOverride) => {
    return all.concat(existingOverride.dateSlots)
  }, [])
  const allDateSlots = override.dateSlots.concat(existingDateSlots)
  return checkOverlappingDateRanges(allDateSlots)
}

export const getReference = (length = 6) => {
  let result           = ''
  let characters       = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
  let charactersLength = characters.length
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength))
  }
  return result
}

export const findRootOrg = (
  orgTree: ITree_Org,
  orgId: string,
) => findParentOrg(
  orgTree,
  orgId,
  (org) => {
    return org.meta?.appconfig?.booking?.is_root_org ? true : false
  },
  true,
)

export const getOrgSettings = (
  orgTree: ITree_Org,
  orgId: string,
) => {
  
}

export const isTimeWithinSlot = (
  time: IData_Booking_Availability_Time,
  slot: IData_Booking_Availability_Time_Slot,
) => {
  if(
    time.hour >= slot.start.hour &&
    time.min >= slot.start.min &&
    time.hour <= slot.end.hour &&
    time.min <= slot.end.min
  ) {
    return true
  }
  else {
    return false
  }
}

export const areTimesEqual = (
  timeA: IData_Booking_Availability_Time,
  timeB: IData_Booking_Availability_Time,
) => {
  return timeA.hour == timeB.hour && timeA.min == timeB.min
}

export const adjustTime = (
  time: IData_Booking_Availability_Time,
  minutes: number,
): IData_Booking_Availability_Time => {


  const dt = new Date()
  dt.setHours(time.hour)
  dt.setMinutes(time.min)
  dt.setSeconds(0)
  dt.setMilliseconds(0)

  const adjustedDt = new Date(dt.getTime() + minutes * 60000)

  return {
    hour: adjustedDt.getHours(),
    min: adjustedDt.getMinutes(),
  }
}

export const doBookingSlotsClash = (
  startA: IData_Booking_Availability_Time,
  lengthA: number,
  startB: IData_Booking_Availability_Time,
  lengthB: number,
): boolean => {
  const endA = adjustTime(startA, lengthA)
  const endB = adjustTime(startB, lengthB)
  const slotA: IData_Booking_Availability_Time_Slot = {
    start: startA,
    end: endA,
  }
  return isTimeWithinSlot(startB, slotA) || isTimeWithinSlot(endB, slotA)
}

// get a date object that can be compared in terms of only hours and minutes
export const getCleanCompareDate = (hour: number, min: number) => {
  const dt = new Date()
  dt.setFullYear(2000)
  dt.setMonth(0)
  dt.setDate(1)
  dt.setHours(hour)
  dt.setMinutes(min)
  dt.setSeconds(0)
  dt.setMilliseconds(0)
  return dt
}

export const doTimeSlotsIntersect = (
  slotA: IData_Booking_Availability_Time_Slot,
  slotB: IData_Booking_Availability_Time_Slot,
): boolean => {

  const startA = getCleanCompareDate(slotA.start.hour, slotA.start.min)
  const endA = getCleanCompareDate(slotA.end.hour, slotA.end.min)

  const startB = getCleanCompareDate(slotB.start.hour, slotB.start.min)
  const endB = getCleanCompareDate(slotB.end.hour, slotB.end.min)

  return (startA.getTime() < endB.getTime()) && (startB.getTime() < endA.getTime())
}

export const getBookingTimeTitle = (
  booking: IRecord_Booking_Core_Viewable,
) => {
  const start = getTimeFromDateString(booking.data.date)
  return getTimeSlotTitle({
    start,
    end: addLengthToTime(start, booking.data.product.length),
  })
}

export const getSlotTimeTitle = (
  slot: IData_Booking_Availability_Time,
  length: number,
) => {
  return getTimeSlotTitle({
    start: slot,
    end: addLengthToTime(slot, length),
  })
}

// test the user personas to see if it contains BOOKING_ET_ROLE_NAME
export const isUserET = (user: INode_User): boolean => {
  return isPersonasET(user.personas)
}

export const isPersonasET = (personas: string[]): boolean => {
  return personas.includes(BOOKING_ET_ROLE_NAME)
}

export const splitCoverageString = (coverage: string): string[] => (coverage || '')
  .split(/\s*,\s*/)
  .filter(s => s ? true : false)
export const checkCoverageOutcode = (outcode: string): boolean => isValid(`${outcode} 1XX`)
export const getOutcode = (postcode: string): string => {
  if(!isValid(postcode)) throw new Error(`invalid postcode: ${postcode}`)
  return parse(postcode).outcode.toLowerCase()
}

export const validatePostcode = (postcode: string): boolean => isValid(postcode)

export const doesETCoverOutcode = (et: IData_Booking_Settings_ET, outcode: string): boolean => {
  if(!outcode) return true
  const parts = splitCoverageString(et.coverage || '')
  const matching = parts.find(part => part.toLowerCase() == outcode)
  return matching ? true : false
}